import { analytics, AnalyticsEventParams } from 'shared/library/analytics';
import { SharePayload } from './share-types';

export async function share(
  data: SharePayload,
  eventParams?: AnalyticsEventParams,
): Promise<{ share_outcome: 'clipboard' | 'handoff' | 'dismissed' | 'failed' }> {
  if (typeof navigator === 'undefined') {
    return { share_outcome: 'failed' };
  }

  const shareData = toShareData(data);

  if (navigator.share && canShare(shareData)) {
    try {
      await navigator.share(shareData);
      analytics.event('share', { method: 'native', ...eventParams });
      return { share_outcome: 'handoff' };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { share_outcome: 'dismissed' };
      }
    }
  }

  const copied = await copyToClipboard(data.url, eventParams);
  return { share_outcome: copied ? 'clipboard' : 'failed' };
}

function toShareData(data: SharePayload): ShareData {
  const shareData: ShareData = {
    title: data.title,
    text: data.message,
    url: data.url,
  };

  if (data.files?.length) {
    shareData.files = data.files as File[];
  }

  return shareData;
}

function canShare(data: ShareData) {
  if (!data.files?.length) {
    return true;
  }

  try {
    return navigator.canShare?.(data) ?? false;
  } catch {
    return false;
  }
}

async function copyToClipboard(
  url: string | undefined,
  eventParams?: AnalyticsEventParams,
): Promise<boolean> {
  if (!url) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      analytics.event('share', { method: 'clipboard', ...eventParams });
      return true;
    }

    if (typeof document === 'undefined') return false;

    const input = document.createElement('textarea');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    let copied = false;
    try {
      input.select();
      copied = document.execCommand('copy');
    } finally {
      input.remove();
    }

    if (copied) {
      analytics.event('share', { method: 'clipboard', ...eventParams });
    }
    return copied;
  } catch {
    return false;
  }
}
